import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { 
  CreditCard, 
  CheckCircle, 
  AlertCircle,
  RefreshCw,
  Unlock,
  AlertTriangle,
  ChevronRight,
  UserX,
  Info,
  Smartphone,
  QrCode,
  KeyRound,
  X,
  Sparkles
} from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import DashboardCard from '../components/DashboardCard';
import Button from '../components/Button';
import Modal from '../components/Modal';

const ANNOUNCEMENT_KEY = 'pin-feature-announcement-dismissed';

/** Live countdown to an ISO timestamp, e.g. "1:32:05". */
const ExpiryCountdown = ({ expiresAt, className = '' }) => {
  const [remaining, setRemaining] = useState(() =>
    expiresAt ? new Date(expiresAt).getTime() - Date.now() : 0
  );

  useEffect(() => {
    if (!expiresAt) return undefined;
    const tick = () => setRemaining(new Date(expiresAt).getTime() - Date.now());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  if (!expiresAt) return null;
  if (remaining <= 0) return <span className={className}>expired</span>;
  const total = Math.floor(remaining / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const text = h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
  return <span className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>{text}</span>;
};

const QuickRegister = () => {
  const [rfidTag, setRfidTag] = useState('');
  const [rawInput, setRawInput] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRegistration, setLastRegistration] = useState(null);
  const [availableCounts, setAvailableCounts] = useState({});
  const [isConverting, setIsConverting] = useState(false);
  const [showQrSheet, setShowQrSheet] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(
    () => localStorage.getItem(ANNOUNCEMENT_KEY) !== '1'
  );

  const dismissAnnouncement = () => {
    localStorage.setItem(ANNOUNCEMENT_KEY, '1');
    setShowAnnouncement(false);
  };
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const isGroupSelected = selectedGroup !== '';

  /**
   * Converts UID from USB reader format to ESP-compatible format.
   * 
   * USB readers may output:
   * - Decimal: 1706663405 -> needs conversion to hex + byte reversal
   * - Hex string: 4955cc2951190 -> already hex, may need truncation/reversal
   * 
   * ESP expects: 8-character uppercase hex with reversed byte order
   */
  const convertUID = useCallback((raw) => {
    const cleaned = (raw || '').split('\n').pop().trim().toUpperCase();
    
    if (!cleaned) return '';
    
    console.log(`UID Input: "${cleaned}" (length: ${cleaned.length})`);
    
    // Check if input is purely decimal (no hex letters)
    const isPureDecimal = /^\d+$/.test(cleaned);
    
    if (isPureDecimal) {
      const dec = parseInt(cleaned, 10);
      if (!isNaN(dec) && dec > 0) {
        // Convert decimal to hex, pad to 8 chars
        const hex = ('00000000' + dec.toString(16).toUpperCase()).slice(-8);
        // Reverse byte order (little-endian to big-endian)
        const reversed = hex.slice(6,8) + hex.slice(4,6) + hex.slice(2,4) + hex.slice(0,2);
        console.log(`UID Conversion (decimal): ${cleaned} -> ${hex} (hex) -> ${reversed} (reversed)`);
        return reversed;
      }
    }
    
    // Input contains hex characters (A-F) - treat as hex string
    // Remove any non-hex characters
    const hexOnly = cleaned.replace(/[^0-9A-F]/gi, '').toUpperCase();
    
    if (hexOnly.length >= 8) {
      // Take last 8 characters (most significant for longer UIDs)
      // Or first 8 if it's a standard 4-byte UID padded
      const hex8 = hexOnly.slice(-8);
      // Reverse byte order
      const reversed = hex8.slice(6,8) + hex8.slice(4,6) + hex8.slice(2,4) + hex8.slice(0,2);
      console.log(`UID Conversion (hex): ${cleaned} -> ${hexOnly} (cleaned) -> ${hex8} (8-char) -> ${reversed} (reversed)`);
      return reversed;
    } else if (hexOnly.length > 0) {
      // Short hex - pad to 8 chars and reverse
      const hex8 = ('00000000' + hexOnly).slice(-8);
      const reversed = hex8.slice(6,8) + hex8.slice(4,6) + hex8.slice(2,4) + hex8.slice(0,2);
      console.log(`UID Conversion (short hex): ${cleaned} -> ${hex8} (padded) -> ${reversed} (reversed)`);
      return reversed;
    }
    
    // Fallback - return as-is
    console.log(`UID Conversion (passthrough): ${cleaned}`);
    return cleaned;
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchAvailableCounts();

    const interval = setInterval(fetchAvailableCounts, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isGroupSelected && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isGroupSelected]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get('/api/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to fetch locker groups');
    }
  };

  const fetchAvailableCounts = async () => {
    try {
      const [lockersRes, groupsRes] = await Promise.all([
        axios.get('/api/lockers'),
        axios.get('/api/groups')
      ]);
      
      const lockers = lockersRes.data;
      const groupsData = groupsRes.data;
      
      const counts = {};
      groupsData.forEach(group => {
        const groupLockerIds = group.locker_ids || [];
        // Only count lockers that are available AND online
        const availableOnline = lockers.filter(l => 
          groupLockerIds.includes(l.id) && 
          l.status === 'available' &&
          l.is_online === 1
        ).length;
        counts[group.id] = availableOnline;
      });
      
      setAvailableCounts(counts);
    } catch (error) {
      console.error('Error fetching available counts:', error);
    }
  };

  const handleSubmit = async (e, convertedTag = null) => {
    if (e) e.preventDefault();
    
    const tagToSubmit = convertedTag || rfidTag;
    
    if (!selectedGroup) {
      toast.error('Please select a locker group first');
      return;
    }
    
    if (!tagToSubmit.trim()) {
      toast.error('Please scan an RFID card');
      return;
    }

    setLoading(true);

    try {
      // Server-owned RFID flow: /api/rfid/scan handles unknown-card guest
      // creation, locker allocation (controller > group > global),
      // and the publishOpenLock pulse in one call.
      const response = await axios.post('/api/rfid/scan', {
        uid: tagToSubmit.trim(),
        reader_id: 'kiosk-frontdesk',
        source: 'usb',
        group_id: parseInt(selectedGroup)
      });

      const data = response.data || {};
      const decision = data.decision;

      if (decision === 'allocated-and-opened' || decision === 'opened-assigned') {
        // Massage the router response into the shape this page already
        // renders, so result/unlock/unassign UI keeps working unchanged.
        setLastRegistration({
          decision,
          user: data.user,
          locker: data.locker,
          expiryDate: data.valid_until,
          pin: data.pin || null,
          alreadyAssigned: decision === 'opened-assigned'
        });
        const verb = decision === 'opened-assigned' ? 'opened (already assigned)' : 'assigned';
        toast.success(`Locker ${data.locker?.name || ''} ${verb}!`);

        setRfidTag('');
        setRawInput('');
        fetchAvailableCounts();
        if (inputRef.current) inputRef.current.focus();
      } else {
        // 'rejected' or 'debounced' -- shouldn't normally happen with a
        // clear group selection, but surface the reason so the operator
        // can fix it (no eligible locker, reader disabled, etc.).
        const reason = data.reason || decision || 'unknown';
        toast.error(`Scan ${decision || 'failed'}: ${reason}`);
        setLastRegistration({ error: reason, decision });
      }
    } catch (error) {
      console.error('Error registering card:', error);
      const errorData = error.response?.data || {};
      const reason = errorData.reason || errorData.error || 'Failed to scan card';

      if (errorData.decision === 'rejected' && errorData.locker) {
        toast.error(`${reason}`);
        setLastRegistration({
          error: reason,
          decision: 'rejected',
          locker: errorData.locker,
          user: errorData.user
        });
      } else {
        toast.error(reason);
        setLastRegistration({ error: reason });
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Card-less assignment: no RFID needed. The guest gets a locker number +
   * PIN; entering them once on the unlock page links their phone, which then
   * opens the locker with swipe-to-unlock.
   */
  const handleAssignCardless = async () => {
    if (!selectedGroup) {
      toast.error('Please select a locker group first');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post('/api/lockers/assign-cardless', {
        group_id: parseInt(selectedGroup)
      });
      const data = response.data || {};
      setLastRegistration({
        decision: 'cardless',
        cardless: true,
        user: data.user,
        locker: data.locker,
        expiryDate: data.expires_at,
        pin: data.pin || null
      });
      toast.success(`Locker ${data.locker?.name || ''} assigned (no card)`);
      fetchAvailableCounts();
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to assign a locker';
      toast.error(msg);
      setLastRegistration({ error: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockLocker = async () => {
    if (!lastRegistration?.locker?.id) return;
    
    try {
      await axios.post(`/api/lockers/${lastRegistration.locker.id}/command`, { 
        command: 'openlock' 
      });
      toast.success(`Locker ${lastRegistration.locker.name} unlocked!`);
    } catch (error) {
      console.error('Error unlocking locker:', error);
      toast.error('Failed to unlock locker');
    }
  };

  const handleUnassignLocker = async () => {
    if (!lastRegistration?.locker?.id) return;
    
    try {
      await axios.post(`/api/lockers/${lastRegistration.locker.id}/unassign`);
      toast.success(`Locker ${lastRegistration.locker.name} unassigned!`);
      setLastRegistration(null);
      setRfidTag('');
      setRawInput('');
      fetchAvailableCounts();
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error('Error unassigning locker:', error);
      toast.error('Failed to unassign locker');
    }
  };

  /**
   * Handle input from USB RFID reader with debounce.
   * USB readers type quickly and send Enter at the end.
   * We wait 300ms after last keystroke to process the full input.
   */
  const handleInputChange = (e) => {
    const val = e.target.value;
    setRawInput(val);
    setIsConverting(true);
    
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Wait 300ms after last keystroke before converting
    debounceRef.current = setTimeout(() => {
      // Strip non-numeric characters to isolate decimal UID from USB reader
      const digitsOnly = val.replace(/[^0-9]/g, '');
      
      let converted;
      if (digitsOnly.length >= 8) {
        // Looks like a decimal UID from USB reader
        converted = convertUID(digitsOnly);
      } else {
        // Might be hex or manual input
        converted = convertUID(val.trim());
      }
      
      setRfidTag(converted);
      setIsConverting(false);
      
      console.log(`RFID Input: raw="${val}" -> digits="${digitsOnly}" -> converted="${converted}"`);
    }, 300);
  };

  /**
   * Handle Enter key - auto-submit after conversion is complete
   * Uses e.target.value directly since React state may not be updated yet
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Clear debounce and process immediately
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      
      // Get the current input value directly from the event target
      // (React state may not be updated yet due to async nature)
      const currentValue = e.target.value;
      console.log(`Enter pressed, current input value: "${currentValue}"`);
      
      // Convert the current input
      const digitsOnly = currentValue.replace(/[^0-9]/g, '');
      let converted;
      if (digitsOnly.length >= 8) {
        converted = convertUID(digitsOnly);
      } else {
        converted = convertUID(currentValue.trim());
      }
      
      console.log(`Enter key conversion: "${currentValue}" -> "${converted}"`);
      
      setRfidTag(converted);
      setRawInput(currentValue);
      setIsConverting(false);
      
      // Submit with the converted tag
      if (converted.trim() && isGroupSelected) {
        handleSubmit(null, converted);
      }
    }
  };

  /**
   * Clear input on focus for fresh scan
   */
  const handleInputFocus = () => {
    setRawInput('');
    setRfidTag('');
  };

  const clearLastRegistration = () => {
    setLastRegistration(null);
    setRfidTag('');
    setRawInput('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getSelectedGroupName = () => {
    const group = groups.find(g => g.id.toString() === selectedGroup);
    return group?.name || '';
  };

  return (
    <div className="p-6">
      <DashboardHeader 
        title="Quick Registration" 
        subtitle="Register guest RFID card and assign locker" 
      />

      {/* New feature announcement: phone unlock with PIN */}
      {showAnnouncement && (
        <div className="relative mb-6 rounded-2xl overflow-hidden material-regular shadow-card animate-fade-up">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(110deg, rgba(10,132,255,0.18) 0%, rgba(94,92,230,0.12) 50%, transparent 85%)'
            }}
          />
          <button
            onClick={dismissAnnouncement}
            className="absolute top-3 right-3 p-2 rounded-full text-secondary hover:text-white hover:bg-white/10 transition-colors z-10"
            aria-label="Dismiss announcement"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="relative p-6">
            <div className="flex items-center space-x-2 mb-4">
              <span className="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full bg-primary-500/20 text-primary-300 text-caption font-semibold uppercase tracking-wide">
                <Sparkles className="w-3 h-3" />
                <span>New</span>
              </span>
              <h2 className="text-title3 text-white">Phone unlock with PIN</h2>
            </div>

            <p className="text-subheadline text-secondary mb-5 max-w-3xl">
              Guests no longer need to walk back to the central scanner. Every
              assignment comes with a PIN that links the guest's phone to their
              locker - one phone per locker - and the phone then opens it with
              swipe-to-unlock, even on cellular. Tell every guest how it works:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl">
              <div className="material-thin rounded-xl p-4 flex items-start space-x-3">
                <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <KeyRound className="w-4 h-4 text-primary-300" />
                </div>
                <div>
                  <p className="text-subheadline font-semibold text-white mb-0.5">1. Get your PIN here</p>
                  <p className="text-footnote text-secondary">
                    With each locker assignment, the screen shows the locker number and a personal PIN.
                  </p>
                </div>
              </div>
              <div className="material-thin rounded-xl p-4 flex items-start space-x-3">
                <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <QrCode className="w-4 h-4 text-primary-300" />
                </div>
                <div>
                  <p className="text-subheadline font-semibold text-white mb-0.5">2. Scan the room QR</p>
                  <p className="text-footnote text-secondary">
                    A QR code is posted in the locker room (and shown below). It opens the unlock page on any phone.
                  </p>
                </div>
              </div>
              <div className="material-thin rounded-xl p-4 flex items-start space-x-3">
                <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-4 h-4 text-primary-300" />
                </div>
                <div>
                  <p className="text-subheadline font-semibold text-white mb-0.5">3. Link once, then swipe</p>
                  <p className="text-footnote text-secondary">
                    Entering the PIN once links the phone to the locker. From
                    then on, a swipe on the phone opens it.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <Button variant="tinted" onClick={() => setShowQrSheet(true)}>
                <QrCode className="w-4 h-4 mr-2" />
                Show room QR
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* QR sheet */}
      <Modal open={showQrSheet} onClose={() => setShowQrSheet(false)} className="text-center">
        <h3 className="text-title3 text-white mb-1">Locker room unlock QR</h3>
        <p className="text-footnote text-secondary mb-5">
          Guests scan this with their phone camera to open the unlock page.
        </p>
        <div className="bg-white p-4 rounded-2xl inline-block">
          <img
            src="/api/public/unlock-qr"
            alt="Locker room unlock QR"
            width={240}
            height={240}
            className="block"
          />
        </div>
      </Modal>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registration Form */}
        <div className="space-y-6">
          {/* Step 1: Group Selection */}
          <DashboardCard>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                  isGroupSelected 
                    ? 'bg-green-500 text-white' 
                    : 'bg-primary-500 text-white animate-pulse'
                }`}>
                  {isGroupSelected ? <CheckCircle className="w-5 h-5" /> : '1'}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Select Locker Area</h2>
                  <p className="text-sm text-gray-400">Choose where to assign the locker</p>
                </div>
              </div>
              {isGroupSelected && (
                <span className="text-green-400 text-sm font-medium">
                  {getSelectedGroupName()} selected
                </span>
              )}
            </div>

            {/* Warning if no group selected */}
            {!isGroupSelected && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-medium text-sm">Select a locker group first</p>
                  <p className="text-amber-400/70 text-xs mt-1">
                    You must choose a locker area before scanning the guest's card
                  </p>
                </div>
              </div>
            )}

            {/* Group Selection Buttons */}
            <div className="grid grid-cols-2 gap-4">
              {groups.map(group => {
                const isSelected = selectedGroup === group.id.toString();
                const availableCount = availableCounts[group.id] || 0;
                const hasAvailable = availableCount > 0;
                
                return (
                  <div
                    key={group.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (hasAvailable) {
                        setSelectedGroup(group.id.toString());
                      } else {
                        toast.error(`No available lockers in ${group.name}`);
                      }
                    }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && hasAvailable) {
                        setSelectedGroup(group.id.toString());
                      }
                    }}
                    style={{ cursor: hasAvailable ? 'pointer' : 'not-allowed' }}
                    className={`relative p-5 rounded-2xl border transition-all duration-200 ease-spring ${
                      isSelected
                        ? 'border-success-500/60 bg-success-500/15 ring-2 ring-success-500/40 shadow-glow-green'
                        : hasAvailable
                          ? 'material-thin hover:border-primary-500/50 hover:bg-white/[0.08] active:scale-[0.98]'
                          : 'border-white/[0.05] bg-white/[0.02] opacity-50'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className={`text-2xl font-bold mb-1 ${isSelected ? 'text-green-400' : 'text-white'}`}>
                      {group.name}
                    </div>
                    <div className={`text-sm ${hasAvailable ? 'text-gray-400' : 'text-red-400'}`}>
                      {hasAvailable ? `${availableCount} lockers available` : 'No lockers available'}
                    </div>
                  </div>
                );
              })}
            </div>
          </DashboardCard>

          {/* Step 2: Scan Card */}
          <DashboardCard className={!isGroupSelected ? 'opacity-50 pointer-events-none' : ''}>
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                isGroupSelected 
                  ? 'bg-primary-500 text-white' 
                  : 'bg-dashboard-600 text-gray-400'
              }`}>
                2
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Scan Guest Card</h2>
                <p className="text-sm text-gray-400">
                  {isGroupSelected 
                    ? `Ready to scan for ${getSelectedGroupName()} locker`
                    : 'Complete step 1 first'
                  }
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <CreditCard className={`w-6 h-6 ${isGroupSelected ? 'text-primary-400' : 'text-gray-600'}`} />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={rawInput}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleInputFocus}
                  placeholder={isGroupSelected ? "Place card on reader..." : "Select a group first"}
                  disabled={!isGroupSelected}
                  className={`w-full pl-14 pr-4 py-5 text-xl rounded-xl transition-all ${
                    isGroupSelected
                      ? 'bg-dashboard-800 border-2 border-primary-500/50 text-white placeholder-gray-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 focus:outline-none'
                      : 'bg-dashboard-900 border-2 border-dashboard-700 text-gray-500 placeholder-gray-600 cursor-not-allowed'
                  }`}
                  autoComplete="off"
                />
                {isConverting && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <RefreshCw className="w-5 h-5 text-primary-400 animate-spin" />
                  </div>
                )}
              </div>
              
              {/* Show converted UID */}
              {rfidTag && (
                <div className="bg-dashboard-800 rounded-lg p-3 border border-dashboard-700">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Converted Card ID:</span>
                    <span className="text-sm font-mono text-green-400">{rfidTag}</span>
                  </div>
                </div>
              )}
              
              {isGroupSelected && (
                <p className="text-xs text-gray-500 text-center">
                  Card will be automatically converted and registered when scanned
                </p>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full py-4 text-lg"
                disabled={loading || !rfidTag.trim() || !isGroupSelected || isConverting}
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    Assigning Locker...
                  </>
                ) : (
                  <>
                    Assign Locker
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Card-less path: phone becomes the key */}
            <div className="flex items-center my-4">
              <div className="flex-1 h-px bg-white/[0.08]" />
              <span className="px-3 text-caption text-muted uppercase tracking-wide">or</span>
              <div className="flex-1 h-px bg-white/[0.08]" />
            </div>
            <Button
              variant="tinted"
              className="w-full py-3.5"
              disabled={loading || !isGroupSelected}
              onClick={handleAssignCardless}
            >
              <Smartphone className="w-5 h-5 mr-2" />
              Assign without card (phone only)
            </Button>
            <p className="text-caption text-muted text-center mt-2">
              The guest gets a locker number and PIN. They scan the printed
              room QR, enter just the PIN once to link their phone, then open
              the locker with swipe-to-unlock.
            </p>
          </DashboardCard>
        </div>

        {/* Result Display */}
        <DashboardCard className="h-fit">
          <div className="flex items-center space-x-3 mb-6">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              lastRegistration?.alreadyAssigned
                ? 'bg-yellow-600/20'
                : lastRegistration?.error 
                  ? 'bg-red-600/20' 
                  : lastRegistration?.locker 
                    ? 'bg-green-600/20' 
                    : 'bg-dashboard-700'
            }`}>
              {lastRegistration?.alreadyAssigned ? (
                <Info className="w-6 h-6 text-yellow-400" />
              ) : lastRegistration?.error ? (
                <AlertCircle className="w-6 h-6 text-red-400" />
              ) : lastRegistration?.locker ? (
                <CheckCircle className="w-6 h-6 text-green-400" />
              ) : (
                <CreditCard className="w-6 h-6 text-gray-500" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {lastRegistration?.alreadyAssigned ? 'Card Already Assigned' : 'Assignment Result'}
              </h2>
              <p className="text-sm text-gray-400">
                {lastRegistration?.alreadyAssigned 
                  ? 'This card has an existing locker assignment'
                  : lastRegistration?.locker 
                    ? 'Locker assigned successfully' 
                    : 'Waiting for card scan...'}
              </p>
            </div>
          </div>

          {lastRegistration?.alreadyAssigned ? (
            <div className="text-center py-4">
              <div className="mb-6">
                <p className="text-gray-400 text-sm mb-2">Currently Assigned To</p>
                <div className="text-7xl font-bold text-yellow-400 mb-2">
                  {lastRegistration.locker?.name}
                </div>
                <p className="text-gray-500">{lastRegistration.message}</p>
              </div>

              <div className="bg-dashboard-800 rounded-xl p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-1">Card ID</p>
                    <p className="text-white font-mono text-lg">{lastRegistration.user?.rfid_tag}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Guest</p>
                    <p className="text-white font-medium text-lg">
                      {lastRegistration.user?.first_name} {lastRegistration.user?.last_name}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  variant="success"
                  className="w-full py-4 text-lg"
                  onClick={handleUnlockLocker}
                >
                  <Unlock className="w-5 h-5 mr-2" />
                  Unlock Locker
                </Button>
                <Button
                  variant="danger"
                  className="w-full py-3"
                  onClick={handleUnassignLocker}
                >
                  <UserX className="w-5 h-5 mr-2" />
                  Unassign Locker
                </Button>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={clearLastRegistration}
                >
                  Scan Different Card
                </Button>
              </div>
            </div>
          ) : lastRegistration?.error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-20 h-20 text-red-400 mx-auto mb-4" />
              <p className="text-xl text-red-400 font-medium mb-2">Registration Failed</p>
              <p className="text-gray-400 mb-6">{lastRegistration.message || lastRegistration.error}</p>
              <Button
                variant="secondary"
                onClick={clearLastRegistration}
              >
                Try Again
              </Button>
            </div>
          ) : lastRegistration?.locker ? (
            <div className="text-center py-4">
              <div className="mb-6">
                <p className="text-gray-400 text-sm mb-2">Assigned Locker</p>
                <div className="text-7xl font-bold text-green-400 mb-2">
                  {lastRegistration.locker.name}
                </div>
                <p className="text-gray-500">{lastRegistration.group}</p>
              </div>

              {lastRegistration.pin && (
                <div className="mb-6 material-regular border-primary-500/30 rounded-2xl p-5 animate-scale-in">
                  <div className="flex items-center justify-center space-x-1.5 mb-2">
                    <Smartphone className="w-4 h-4 text-primary-300" />
                    <p className="text-footnote text-label">Phone-link PIN</p>
                  </div>
                  <div
                    className="text-5xl font-extrabold tracking-[0.3em] text-primary-300 mb-4"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {lastRegistration.pin}
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <div className="bg-white p-2 rounded-xl flex-shrink-0">
                      <img
                        src="/api/public/unlock-qr"
                        alt="Unlock page QR"
                        width={96}
                        height={96}
                        className="block"
                      />
                    </div>
                    <p className="text-footnote text-secondary text-left max-w-[200px]">
                      Scan the QR and enter this PIN once. That links the phone
                      to locker{' '}
                      <span className="text-white font-semibold">{lastRegistration.locker.name}</span>{' '}
                      - then just swipe to unlock. One phone per locker.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-dashboard-800 rounded-xl p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-1">{lastRegistration.cardless ? 'Access' : 'Card ID'}</p>
                    {lastRegistration.cardless ? (
                      <p className="text-primary-300 font-medium text-lg">Phone only</p>
                    ) : (
                      <p className="text-white font-mono text-lg">{lastRegistration.user?.rfid_tag}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Expires in</p>
                    {lastRegistration.expiryDate ? (
                      <ExpiryCountdown
                        expiresAt={lastRegistration.expiryDate}
                        className="text-green-400 font-medium text-lg"
                      />
                    ) : (
                      <p className="text-green-400 font-medium text-lg">Ready</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  variant="success"
                  className="w-full py-4 text-lg"
                  onClick={handleUnlockLocker}
                >
                  <Unlock className="w-5 h-5 mr-2" />
                  Unlock Locker Now
                </Button>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={clearLastRegistration}
                >
                  Register Next Guest
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-dashboard-800 flex items-center justify-center">
                <CreditCard className="w-12 h-12 text-gray-600" />
              </div>
              <p className="text-gray-400 text-lg mb-2">No card scanned yet</p>
              <p className="text-gray-600 text-sm">
                {isGroupSelected 
                  ? 'Place guest card on the reader to assign a locker'
                  : 'Select a locker group to get started'
                }
              </p>
            </div>
          )}
        </DashboardCard>
      </div>

      {/* Locker-room QR for the phone unlock page */}
      <div className="mt-6">
        <DashboardCard>
          <div className="flex items-center space-x-2 mb-4">
            <QrCode className="w-5 h-5 text-primary-300" />
            <h3 className="text-title3 text-white">Locker Room Unlock QR</h3>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="bg-white p-3 rounded-2xl">
              <img
                src="/api/public/unlock-qr"
                alt="Locker room unlock QR"
                width={180}
                height={180}
                className="block"
              />
            </div>
            <div className="text-subheadline text-secondary space-y-2">
              <p>
                Print this code and post it in the locker room. Guests scan it
                with their phone, enter just their PIN once to link the phone,
                then swipe to unlock - works over cellular.
              </p>
              <p className="text-footnote text-muted">
                The code points at the configured public unlock URL
                (PUBLIC_UNLOCK_URL / Tailscale Funnel). One QR serves the whole
                room.
              </p>
            </div>
          </div>
        </DashboardCard>
      </div>

      {/* Available Lockers Summary */}
      <div className="mt-6">
        <DashboardCard>
          <h3 className="text-lg font-semibold text-white mb-4">Locker Availability</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {groups.map(group => {
              const count = availableCounts[group.id] || 0;
              const isSelected = selectedGroup === group.id.toString();
              return (
                <div 
                  key={group.id}
                  className={`rounded-xl p-4 text-center transition-all ${
                    isSelected 
                      ? 'bg-green-500/20 border-2 border-green-500/50' 
                      : 'bg-dashboard-800'
                  }`}
                >
                  <div className={`text-3xl font-bold ${count > 0 ? 'text-white' : 'text-red-400'}`}>
                    {count}
                  </div>
                  <div className={`text-sm ${isSelected ? 'text-green-400' : 'text-gray-400'}`}>
                    {group.name}
                  </div>
                </div>
              );
            })}
          </div>
        </DashboardCard>
      </div>
    </div>
  );
};

export default QuickRegister;
